import {
  Input,
  Output,
  EventEmitter,
  Component,
  OnInit,
  OnDestroy,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';

import { DatePickerModule } from 'primeng/datepicker';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { SelectButtonModule } from 'primeng/selectbutton';
import { forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Importação oficial do Leaflet instalado via npm
import * as L from 'leaflet';

import {
  PostoService,
  ClienteService,
  ContratoService,
  MunicipioService,
  TipoPostoService,
  ProvinciaService,
  TipoEquipService,
} from '@core/services/domain-services';
import {
  Cliente,
  ContratoCliente,
  Municipio,
  Provincia,
  Posto,
  TipoEquipamento,
  TipoPosto,
  TipoServicoContrato,
  ModeloCobrancaContrato,
} from '@core/models/models';
import {
  EnumService,
  TipoServicoOption,
} from '@shared/api/services/enum.service';
import { calcularCobrancaContrato } from '@shared/utils/contrato-cobranca.util';

function fimAposInicio(
  group: AbstractControl,
): { [key: string]: boolean } | null {
  const inicio = group.get('inicioContrato')?.value;
  const fim = group.get('fimContrato')?.value;
  if (!inicio || !fim) return null;
  return new Date(fim) > new Date(inicio) ? null : { fimAnterior: true };
}

function validarModeloCobranca(
  group: AbstractControl,
): { [key: string]: boolean } | null {
  const modelo = group.get('modeloCobranca')?.value;
  if (!modelo) return null;
  if (modelo === 'fixo_mensal') {
    return Number(group.get('valorMensal')?.value) > 0
      ? null
      : { modeloIncompleto: true };
  }
  const valorUnitario = Number(group.get('valorUnitario')?.value) || 0;
  if (valorUnitario <= 0) return { modeloIncompleto: true };
  const qtdCtrl =
    modelo === 'por_efetivo'
      ? group.get('numEfetivos')
      : modelo === 'por_posto'
        ? group.get('numPostos')
        : group.get('numHoras');
  return Number(qtdCtrl?.value) > 0 ? null : { modeloIncompleto: true };
}

@Component({
  selector: 'app-criar-posto',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    InputNumberModule,
    TextareaModule,
    DatePickerModule,
    AutoCompleteModule,
    SelectButtonModule,
  ],

  templateUrl: './criar-posto.component.html',
  styleUrl: './criar-posto.component.scss',
})
export class CriarPostoComponent implements OnInit, OnDestroy {
  @Input() drawerMode = false;
  @Input() presetClienteId: number | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Posto>();

  @ViewChild('contratoFileInput')
  contratoFileInputRef!: ElementRef<HTMLInputElement>;

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly msg = inject(MessageService);
  private readonly postoSvc = inject(PostoService);
  private readonly clienteSvc = inject(ClienteService);
  private readonly contratoSvc = inject(ContratoService);
  private readonly municipioSvc = inject(MunicipioService);
  private readonly tipoSvc = inject(TipoPostoService);
  private readonly provinciaSvc = inject(ProvinciaService);
  private readonly tipoEquipSvc = inject(TipoEquipService);
  private readonly enumSvc = inject(EnumService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  private _activeStep = 0;
  submitting = false;
  private submitted = false;
  fixedClienteId: number | null = null;
  clienteFixo = false;

  editMode = false;
  editPostoId: number | null = null;
  loadingClientes = false;
  loadingContratos = false;
  loadingDropdowns = false;
  loadingMunicipios = false;
  showNovoContrato = false;
  showLocalizacao = true;
  contratoFile: File | null = null;
  contratoFileRequiredError = false;

  readonly modeloCobrancas = [
    { label: 'Fixo Mensal', value: 'fixo_mensal' },
    { label: 'Por Efetivo', value: 'por_efetivo' },
    { label: 'Por Posto', value: 'por_posto' },
    { label: 'Por Hora', value: 'por_hora' },
  ];
  modeloCobranca: string = 'fixo_mensal';
  tiposServico: TipoServicoOption[] = [];

  clientes: Cliente[] = [];
  clientesFiltrados: Cliente[] = [];
  clienteSelecionado: Cliente | null = null;
  clienteAutocompleteVal: any = null;
  contratos: ContratoCliente[] = [];
  contratoSelecionado: ContratoCliente | null = null;
  provincias: Provincia[] = [];
  municipios: Municipio[] = [];
  municipiosFiltrados: Municipio[] = [];
  selectedProvinciaId: number | null = null;
  tiposPostos: TipoPosto[] = [];

  tiposEquipamento: TipoEquipamento[] = [];
  listaEquipamentos: {
    idTipoEquipamento: number;
    designacao: string;
    quantidade: number;
    icon?: string | null;
  }[] = [];
  loadingEquip = false;

  // Propriedades fortemente tipadas com o ecossistema do Leaflet
  private map!: L.Map;
  private mapMarker!: L.Marker;
  private mapTimer: any;
  private provinciaCoords: { lat: number; lng: number } | null = null;

  readonly steps = [
    { title: 'Cliente', subtitle: 'Seleccionar o cliente' },
    { title: 'Contrato', subtitle: 'Associar um contrato' },
    { title: 'Posto', subtitle: 'Dados do posto' },
    { title: 'Revisão', subtitle: 'Confirmar e criar' },
  ];

  readonly riscosOptions = [
    {
      label: 'Baixo',
      value: 'Baixo',
      color: '#2ea043',
      icon: 'fas fa-shield-halved',
    },
    {
      label: 'Moderado',
      value: 'Moderado',
      color: '#d29922',
      icon: 'fas fa-shield',
    },
    { label: 'Alto', value: 'Alto', color: '#e67e22', icon: 'fas fa-shield' },
    {
      label: 'Crítico',
      value: 'Critico',
      color: '#c0392b',
      icon: 'fas fa-shield-halved',
    },
  ];

  clienteForm!: FormGroup;
  contratoForm!: FormGroup;
  novoContratoForm!: FormGroup;
  postoForm!: FormGroup;

  private readonly mockClientes: Cliente[] = [
    { id: 1, nome: 'Banco BFA, S.A.', nif: '5000000001' },
    { id: 2, nome: 'Sonangol EP', nif: '5000000002' },
    { id: 3, nome: 'Angola LNG, S.A.', nif: '5000000003' },
    { id: 4, nome: 'TAAG – Linhas Aéreas', nif: '5000000004' },
  ];
  private readonly mockProvincias: Provincia[] = [
    { id: 1, designacao: 'Luanda' },
    { id: 2, designacao: 'Benguela' },
    { id: 3, designacao: 'Huíla' },
    { id: 4, designacao: 'Cabinda' },
  ];
  private readonly mockMunicipios: Municipio[] = [
    { id: 1, designacao: 'Ingombota', idProvincia: 1 },
    { id: 2, designacao: 'Cacuaco', idProvincia: 1 },
    { id: 3, designacao: 'Viana', idProvincia: 1 },
    { id: 4, designacao: 'Talatona', idProvincia: 1 },
    { id: 5, designacao: 'Belas', idProvincia: 1 },
    { id: 6, designacao: 'Lobito', idProvincia: 2 },
  ];
  private readonly mockTipos: TipoPosto[] = [
    { id: 1, designacao: 'Posto Fixo' },
    { id: 2, designacao: 'Posto Móvel' },
    { id: 3, designacao: 'Posto Temporário' },
    { id: 4, designacao: 'Posto Especial' },
  ];

  get activeStep(): number {
    return this._activeStep;
  }
  set activeStep(value: number) {
    this._activeStep = value;
    // Se o utilizador navegar para o Passo 2 (Dados do Posto), renderiza o mapa imediatamente
    if (value === 2) {
      setTimeout(() => this.initLeafletMap(), 50);
    }
  }

  ngOnInit(): void {
    this.initForms();
    this.loadClientes();
    this.loadDropdowns();
    this.loadTiposServico();

    const idParam = this.drawerMode
      ? null
      : this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.editMode = true;
      this.editPostoId = Number(idParam);
      this.loadPostoParaEdicao(this.editPostoId);
      return;
    }

    const routeClienteId = this.route.snapshot.queryParamMap.get('clienteId');
    const clienteId =
      this.presetClienteId ?? (routeClienteId ? Number(routeClienteId) : null);
    if (clienteId) {
      const cid = Number(clienteId);
      this.fixedClienteId = cid;
      this.clienteFixo = true;
      this.clienteForm.patchValue({ idCliente: cid });
      this.clienteSvc
        .getById(cid)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => {
            const c: Cliente = res?.data ?? res;
            this.clienteSelecionado = c;
            this.clienteAutocompleteVal = c;
            this.loadContratos(cid);
          },
          error: () => {
            this.loadContratos(cid);
          },
        });
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.mapTimer);
    if (this.map) {
      this.map.remove();
    }
  }

  loadPostoParaEdicao(id: number): void {
    this.postoSvc
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const p: any = res?.data ?? res;

          if (p.idCliente) {
            this.clienteForm.patchValue({ idCliente: p.idCliente });
            const c = {
              id: p.idCliente,
              nome: p.nomeCliente ?? `Cliente #${p.idCliente}`,
            };
            this.clienteSelecionado = c;
            this.clienteAutocompleteVal = c;
            this.loadContratos(p.idCliente);
          }

          if (p.idContrato) {
            this.contratoForm.patchValue({ idContrato: p.idContrato });
            this.contratoSelecionado = {
              idCliente: p.idCliente,
              inicioContrato: '',
              fimContrato: '',
              valorMensal: 0,
              id: p.idContrato,
            };
          }

          if (p.nomeMunicipio && p.idMunicipio) {
            this.municipiosFiltrados = [
              { id: p.idMunicipio, designacao: p.nomeMunicipio },
            ];
          }

          this.postoForm.patchValue({
            nome: p.nome,
            idTipoPosto: p.idTipoPosto,
            endereco: p.endereco ?? '',
            idMunicipio: p.idMunicipio,
            latitude: p.latitude ?? -8.838333,
            longitude: p.longitude ?? 13.234444,
            numEfetivosturno: p.numEfetivosTurno ?? 1,
            numTurnosDia: p.numTurnosDia ?? 1,
            instrucoesEspeciais: p.instrucoesEspeciais ?? '',
            risco: p.risco ?? 'Baixo',
          });

          if (p.tiposEquipamentos && p.tiposEquipamentos.length) {
            this.listaEquipamentos = p.tiposEquipamentos
              .map((te: any) => {
                const idTipoEquipamento =
                  te.idTipoEquipamento ??
                  te.tipoEquipamentoId ??
                  te.tipoEquipamento?.id;
                return {
                  idTipoEquipamento,
                  designacao:
                    te.nomeTipoEquipamento ||
                    te.tipoEquipamentoDescricao ||
                    te.designacao ||
                    te.tipoEquipamento?.designacao ||
                    `Equipamento #${idTipoEquipamento}`,
                  quantidade: Math.max(1, Number(te.quantidade ?? 1)),
                  icon: te.icon ?? te.tipoEquipamento?.icon ?? null,
                };
              })
              .filter((te: any) =>
                Number.isFinite(Number(te.idTipoEquipamento)),
              );
          }

          this.activeStep = 2;
          this.cdr.detectChanges();
        },
        error: () => {
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível carregar o posto.',
            life: 4000,
          });
        },
      });
  }

  initForms(): void {
    this.clienteForm = this.fb.group({
      idCliente: [null, Validators.required],
    });
    this.contratoForm = this.fb.group({
      idContrato: [null, Validators.required],
    });
    this.novoContratoForm = this.fb.group(
      {
        tipoServico: [null, Validators.required],
        modeloCobranca: ['fixo_mensal', Validators.required],
        valorMensal: [null],
        numPostos: [0],
        valorUnitario: [null],
        numEfetivos: [null],
        numHoras: [null],
        inicioContrato: [null, Validators.required],
        fimContrato: [null, Validators.required],
        dataFaturacao: [null],
      },
      { validators: [fimAposInicio, validarModeloCobranca] },
    );
    this.postoForm = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(3)]],
      idTipoPosto: [null, Validators.required],
      endereco: [''],
      idMunicipio: [null, Validators.required],
      latitude: [-8.838333],
      longitude: [13.234444],
      numEfetivosturno: [1, [Validators.required, Validators.min(1)]],
      numTurnosDia: [1, [Validators.required, Validators.min(1)]],
      instrucoesEspeciais: [''],
      risco: ['Baixo', Validators.required],
    });
  }

  // ── INICIALIZAÇÃO DO MAPA USANDO O LEAFLET DO NPM ───────────────────
  private initLeafletMap(): void {
    const mapContainer = document.getElementById('mapa-interativo');
    if (!mapContainer) return;

    // Evita duplicar ou recriar instâncias sobre o mesmo container DOM
    if (this.map) {
      this.map.remove();
    }

    const formLat = this.postoForm.get('latitude')?.value;
    const formLng = this.postoForm.get('longitude')?.value;

    // Se há uma província selecionada, centrar aí com zoom de nível provincial;
    // caso contrário usar as coordenadas do formulário (ou o padrão Luanda).
    const [initLat, initLng, initZoom] = this.provinciaCoords
      ? [this.provinciaCoords.lat, this.provinciaCoords.lng, 9]
      : [formLat || -8.838333, formLng || 13.234444, 13];

    this.map = L.map('mapa-interativo').setView([initLat, initLng], initZoom);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(this.map);

    // Ajuste dos caminhos de imagens do marcador (Resolve a quebra de ícones padrão no build do Angular)
    const defaultIcon = L.icon({
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl:
        'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
    });

    this.mapMarker = L.marker([initLat, initLng], {
      icon: defaultIcon,
      draggable: true,
    }).addTo(this.map);

    // Evento 1: Extrair coordenadas clicando em qualquer ponto do mapa
    this.map.on('click', (e: L.LeafletMouseEvent) => {
      this.updateFormCoordinates(e.latlng.lat, e.latlng.lng);
    });

    // Evento 2: Extrair coordenadas arrastando o marcador existente
    this.mapMarker.on('dragend', () => {
      const position = this.mapMarker.getLatLng();
      this.updateFormCoordinates(position.lat, position.lng);
    });
  }

  private updateFormCoordinates(lat: number, lng: number): void {
    const cleanLat = Number(lat.toFixed(6));
    const cleanLng = Number(lng.toFixed(6));

    this.postoForm.patchValue({
      latitude: cleanLat,
      longitude: cleanLng,
    });

    if (this.mapMarker) {
      this.mapMarker.setLatLng([lat, lng]);
    }

    this.cdr.detectChanges();
  }

  onCoordsChange(): void {
    clearTimeout(this.mapTimer);
    this.mapTimer = setTimeout(() => {
      const lat = this.postoForm.get('latitude')?.value;
      const lng = this.postoForm.get('longitude')?.value;
      if (this.map && this.mapMarker && lat && lng) {
        this.map.setView([lat, lng], this.map.getZoom());
        this.mapMarker.setLatLng([lat, lng]);
      }
    }, 800);
  }

  usarLocalizacaoActual(): void {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      this.updateFormCoordinates(lat, lng);
      if (this.map) {
        this.map.setView([lat, lng], 15);
      }
    });
  }

  // ── MÉTODOS DE NEGÓCIO E AUXILIARES ──────────────────────────
  loadClientes(): void {
    this.loadingClientes = true;
    this.clienteSvc
      .getAll({ pageSize: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.clientes = res.data?.length ? res.data : this.mockClientes;
          this.loadingClientes = false;
        },
        error: () => {
          this.clientes = this.mockClientes;
          this.loadingClientes = false;
        },
      });
  }

  loadDropdowns(): void {
    this.loadingDropdowns = true;
    this.loadingEquip = true;
    forkJoin({
      provincias: this.provinciaSvc.getAll({ pageSize: 100 }),
      tipos: this.tipoSvc.getAll({ pageSize: 100 }),
      tiposEquip: this.tipoEquipSvc.getAll({ pageSize: 200 }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.provincias = (res.provincias as any).data?.length
            ? (res.provincias as any).data
            : this.mockProvincias;
          this.tiposPostos = res.tipos.data?.length
            ? res.tipos.data
            : this.mockTipos;
          this.municipios = this.mockMunicipios;
          this.tiposEquipamento = (res.tiposEquip as any).data ?? [];
          this.loadingDropdowns = false;
          this.loadingEquip = false;
        },
        error: () => {
          this.provincias = this.mockProvincias;
          this.municipios = this.mockMunicipios;
          this.tiposPostos = this.mockTipos;
          this.tiposEquipamento = [];
          this.loadingDropdowns = false;
          this.loadingEquip = false;
        },
      });
  }

  private loadTiposServico(): void {
    this.enumSvc
      .getTipoServico()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (opcoes) => {
          this.tiposServico = opcoes;
          this.cdr.markForCheck();
        },
        error: () => {
          this.tiposServico = [];
        },
      });
  }

  onProvinciaChange(idProvincia: number | null): void {
    this.selectedProvinciaId = idProvincia;
    this.postoForm.get('idMunicipio')?.setValue(null);
    this.municipiosFiltrados = [];

    if (!idProvincia) {
      this.provinciaCoords = null;
      return;
    }

    // Focar o mapa na província selecionada usando as coordenadas da API
    const provincia = this.provincias.find((p) => p.id === idProvincia);
    if (provincia?.latitude != null && provincia?.longitude != null) {
      this.provinciaCoords = {
        lat: provincia.latitude,
        lng: provincia.longitude,
      };
      if (this.map) {
        this.map.flyTo([provincia.latitude, provincia.longitude], 9, {
          duration: 1.2,
        });
      }
    }

    this.loadingMunicipios = true;
    this.municipioSvc
      .getByProvincia(idProvincia)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list) => {
          this.municipiosFiltrados = Array.isArray(list)
            ? list
            : ((list as any).data ?? []);
          this.loadingMunicipios = false;
        },
        error: () => {
          this.municipiosFiltrados = this.mockMunicipios.filter(
            (m) => m.idProvincia === idProvincia,
          );
          this.loadingMunicipios = false;
        },
      });
  }

  pesquisarClientes(event: any): void {
    const q = (event.query || '').toLowerCase().trim();
    this.clientesFiltrados = !q
      ? [...this.clientes]
      : this.clientes.filter(
          (c) =>
            (c.nome ?? '').toLowerCase().includes(q) ||
            (c.nif ?? '').toLowerCase().includes(q),
        );
  }

  selecionarCliente(ev: any): void {
    const cliente: Cliente = ev?.value ?? ev;
    if (!cliente?.id) return;
    this.clienteSelecionado = cliente;
    this.clienteAutocompleteVal = cliente;
    this.clienteForm.patchValue({ idCliente: cliente.id });
    this.contratos = [];
    this.contratoSelecionado = null;
    this.contratoForm.reset();
    this.loadContratos(cliente.id!);
  }

  limparCliente(): void {
    this.clienteSelecionado = null;
    this.clienteAutocompleteVal = null;
    this.clienteForm.patchValue({ idCliente: null });
    this.contratos = [];
    this.contratoSelecionado = null;
  }
  
  loadContratos(idCliente: number): void {
    this.loadingContratos = true;
    this.contratoSvc
      .getByCliente(idCliente)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const lista: ContratoCliente[] = Array.isArray(res)
            ? res
            : (res.data ?? []);

          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);

          // Filtra contratos com data válida e ignora 'ativo' se for explicitamente false
          this.contratos = lista.filter((c) => {
            const contratoAny = c as any;
            if (contratoAny.ativo === false) return false;
            if (!c.fimContrato) return true; // Contratos válidos por tempo indeterminado

            const dataFim = new Date(c.fimContrato);
            return dataFim >= hoje;
          });

          this.loadingContratos = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.contratos = [];
          this.loadingContratos = false;
        },
      });
  }

  selecionarContrato(c: ContratoCliente): void {
    this.contratoSelecionado = c;
    this.contratoForm.patchValue({ idContrato: c.id });
    this.showNovoContrato = false;
  }

  get ncf(): { [key: string]: AbstractControl } {
    return this.novoContratoForm?.controls ?? {};
  }

  get totalMensalCalculado(): number {
    const v = this.novoContratoForm?.value;
    if (!v) return 0;
    const vu = Number(v.valorUnitario) || 0;
    switch (this.modeloCobranca) {
      case 'por_efetivo':
        return vu * (Number(v.numEfetivos) || 0);
      case 'por_posto':
        return vu * (Number(v.numPostos) || 0);
      case 'por_hora':
        return vu * (Number(v.numHoras) || 0);
      default:
        return 0;
    }
  }

  onModeloChange(evt?: any): void {
    this.modeloCobranca = evt?.value ?? 'fixo_mensal';
    this.novoContratoForm.get('numPostos')?.setValue(0);
    this.novoContratoForm.get('valorMensal')?.setValue(null);
    this.novoContratoForm.get('valorUnitario')?.setValue(null);
    this.novoContratoForm.get('numEfetivos')?.setValue(null);
    this.novoContratoForm.get('numHoras')?.setValue(null);
  }

  toggleNovoContrato(): void {
    this.showNovoContrato = !this.showNovoContrato;
    if (this.showNovoContrato) {
      this.modeloCobranca = 'fixo_mensal';
      this.novoContratoForm.patchValue({
        modeloCobranca: 'fixo_mensal',
        numPostos: 0,
        valorUnitario: null,
        numEfetivos: null,
        numHoras: null,
      });
    }
  }

  get contratoDuracaoMeses(): number {
    const inicio = this.novoContratoForm?.get('inicioContrato')?.value;
    const fim = this.novoContratoForm?.get('fimContrato')?.value;
    if (!inicio || !fim) return 0;
    const i = new Date(inicio),
      f = new Date(fim);
    if (f <= i) return 0;
    return (
      (f.getFullYear() - i.getFullYear()) * 12 + (f.getMonth() - i.getMonth())
    );
  }

  triggerContratoFileInput(): void {
    this.contratoFileInputRef?.nativeElement.click();
  }

  onContratoFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      this.contratoFile = input.files[0];
      this.contratoFileRequiredError = false;
    }
  }

  removeContratoFile($event: Event): void {
    $event.stopPropagation();
    this.contratoFile = null;
    this.contratoFileRequiredError = true;
    if (this.contratoFileInputRef?.nativeElement)
      this.contratoFileInputRef.nativeElement.value = '';
  }

  criarContrato(): void {
    if (!this.contratoFile) {
      this.contratoFileRequiredError = true;
      return;
    }
    if (this.novoContratoForm.invalid) {
      this.novoContratoForm.markAllAsTouched();
      return;
    }
    const v = this.novoContratoForm.value;

    const cobranca = calcularCobrancaContrato({
      modelo: v.modeloCobranca,
      valorMensal: v.valorMensal,
      valorUnitario: v.valorUnitario,
      numEfetivos: v.numEfetivos,
      numPostos: v.numPostos,
      numHoras: v.numHoras,
    });

    const payload: {
      idCliente: number;
      tipoServico: TipoServicoContrato;
      modeloCobranca: ModeloCobrancaContrato;
      valorUnitario: number;
      quantidade: number;
      descricaoFaturacao: string;
      valorMensal: number;
      numPostos: number;
      inicioContrato: string;
      fimContrato: string;
      diaFaturacao?: string;
    } = {
      idCliente: Number(this.clienteForm.get('idCliente')?.value),
      tipoServico: v.tipoServico,
      modeloCobranca: cobranca.modeloCobranca,
      valorUnitario: cobranca.valorUnitario,
      quantidade: cobranca.quantidade,
      descricaoFaturacao: cobranca.descricaoFaturacao,
      valorMensal: cobranca.valorMensal,
      numPostos: v.numPostos ?? 0,
      inicioContrato: this.fmt(v.inicioContrato),
      fimContrato: this.fmt(v.fimContrato),
      diaFaturacao: v.dataFaturacao ? this.fmt(v.dataFaturacao) : undefined,
    };
    this.contratoSvc
      .createWithFile(payload, this.contratoFile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (novo: any) => {
          this.msg.add({
            severity: 'success',
            summary: 'Contrato criado',
            detail: 'Contrato adicionado com sucesso.',
            life: 3000,
          });
          this.showNovoContrato = false;
          this.novoContratoForm.reset();
          this.contratoFile = null;
          const idCliente = this.clienteForm.get('idCliente')?.value;
          this.loadContratos(idCliente);
          if (novo?.id) setTimeout(() => this.selecionarContrato(novo), 600);
        },
        error: () =>
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao criar contrato.',
            life: 3000,
          }),
      });
  }

  getTipoEquipamentoSelecionado(
    idTipoEquipamento?: number,
  ): (typeof this.listaEquipamentos)[number] | undefined {
    if (idTipoEquipamento == null) return undefined;
    return this.listaEquipamentos.find(
      (item) => item.idTipoEquipamento === idTipoEquipamento,
    );
  }

  isTipoEquipamentoSelecionado(idTipoEquipamento?: number): boolean {
    return !!this.getTipoEquipamentoSelecionado(idTipoEquipamento);
  }

  toggleTipoEquipamento(tipo: TipoEquipamento): void {
    if (tipo.id == null) return;

    if (this.isTipoEquipamentoSelecionado(tipo.id)) {
      this.removerEquipamentoDaLista(tipo.id);
      return;
    }

    this.listaEquipamentos = [
      ...this.listaEquipamentos,
      {
        idTipoEquipamento: tipo.id,
        designacao: tipo.designacao?.trim() || `Equipamento #${tipo.id}`,
        quantidade: 1,
        icon: tipo.icon ?? null,
      },
    ];
  }

  updateEquipamentoQuantidade(
    idTipoEquipamento: number,
    quantidade: number | string | null,
  ): void {
    const item = this.getTipoEquipamentoSelecionado(idTipoEquipamento);
    if (!item) return;
    item.quantidade = Math.max(1, Number(quantidade) || 1);
    this.listaEquipamentos = [...this.listaEquipamentos];
  }

  incrementarEquipamento(idTipoEquipamento: number): void {
    const item = this.getTipoEquipamentoSelecionado(idTipoEquipamento);
    if (item)
      this.updateEquipamentoQuantidade(idTipoEquipamento, item.quantidade + 1);
  }

  decrementarEquipamento(idTipoEquipamento: number): void {
    const item = this.getTipoEquipamentoSelecionado(idTipoEquipamento);
    if (item)
      this.updateEquipamentoQuantidade(idTipoEquipamento, item.quantidade - 1);
  }

  removerEquipamentoDaLista(idTipoEquipamento: number): void {
    this.listaEquipamentos = this.listaEquipamentos.filter(
      (e) => e.idTipoEquipamento !== idTipoEquipamento,
    );
  }

  getEquipIcon(tipo: TipoEquipamento | undefined): string {
    return tipo?.icon?.trim() || 'pi pi-box';
  }

  get totalEquipamentosNecessarios(): number {
    return this.listaEquipamentos.reduce(
      (total, item) => total + item.quantidade,
      0,
    );
  }

  cancel(): void {
    if (this.drawerMode) {
      this.closed.emit();
      return;
    }

    const destination =
      this.editMode && this.editPostoId
        ? ['/admin/postos', this.editPostoId]
        : ['/admin/postos'];
    this.router.navigate(destination);
  }

  nextStep(): void {
    if (this.activeStep === 0 && this.clienteForm.invalid) {
      this.clienteForm.markAllAsTouched();
      return;
    }
    if (this.activeStep === 1 && this.contratoForm.invalid) {
      this.contratoForm.markAllAsTouched();
      return;
    }
    if (this.activeStep === 2 && this.postoForm.invalid) {
      this.postoForm.markAllAsTouched();
      return;
    }
    if (this.activeStep < 3) this.activeStep++;
  }

  prevStep(): void {
    if (this.activeStep > 0) this.activeStep--;
  }
  goToStep(n: number): void {
    if (this.editMode || n < this.activeStep) this.activeStep = n;
  }

  onSubmit(): void {
    if (
      this.postoForm.invalid ||
      this.clienteForm.invalid ||
      this.contratoForm.invalid
    )
      return;
    if (this.submitted || this.submitting) return;
    this.submitted = true;
    this.submitting = true;
    const v = this.postoForm.value;
    const payload: any = {
      nome: v.nome,
      idTipoPosto: v.idTipoPosto,
      endereco: v.endereco || '',
      idMunicipio: v.idMunicipio,
      latitude: v.latitude ?? 0,
      longitude: v.longitude ?? 0,
      idCliente: this.clienteForm.get('idCliente')?.value,
      numEfetivosturno: v.numEfetivosturno,
      numTurnosDia: v.numTurnosDia,
      instrucoesEspeciais: v.instrucoesEspeciais || '',
      idContrato: this.contratoForm.get('idContrato')?.value,
      risco:
        ({ Baixo: 1, Moderado: 2, Alto: 3, Critico: 4 } as Record<
          string,
          number
        >)[String(v.risco)] ?? 1,
      ...(this.listaEquipamentos.length && {
        tiposEquipamentos: this.listaEquipamentos.map((e) => ({
          idTipoEquipamento: e.idTipoEquipamento,
          quantidade: e.quantidade,
        })),
      }),
    };

    if (this.editMode && this.editPostoId) {
      this.postoSvc
        .update(this.editPostoId, payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.msg.add({
              severity: 'success',
              summary: 'Posto actualizado',
              detail: `${payload.nome} guardado com sucesso.`,
              life: 3000,
            });
            setTimeout(
              () => this.router.navigate(['/admin/postos', this.editPostoId]),
              1200,
            );
          },
          error: (err: any) => {
            this.submitting = false;
            this.submitted = false;
            this.msg.add({
              severity: 'error',
              summary: 'Erro',
              detail:
                err?.error?.message || 'Não foi possível guardar o posto.',
              life: 4000,
            });
          },
        });
      return;
    }

    this.postoSvc
      .create(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.msg.add({
            severity: 'success',
            summary: 'Posto criado',
            detail: `${payload.nome} registado com sucesso.`,
            life: 3000,
          });
          if (this.drawerMode) {
            this.saved.emit(created);
            this.closed.emit();
            return;
          }
          const dest = this.fixedClienteId
            ? ['/admin/clientes', this.fixedClienteId]
            : ['/admin/postos'];
          setTimeout(() => this.router.navigate(dest), 1200);
        },
        error: (err: any) => {
          this.submitting = false;
          this.submitted = false;
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: err?.error?.message || 'Não foi possível criar o posto.',
            life: 4000,
          });
        },
      });
  }

  get tipoPostoNome(): string {
    return (
      this.tiposPostos.find(
        (t) => t.id === this.postoForm.get('idTipoPosto')?.value,
      )?.designacao || '—'
    );
  }
  get municipioNome(): string {
    const id = this.postoForm.get('idMunicipio')?.value;
    return (
      this.municipiosFiltrados.find((m) => m.id === id)?.designacao ||
      this.municipios.find((m) => m.id === id)?.designacao ||
      '—'
    );
  }
  get contratoLabel(): string {
    if (!this.contratoSelecionado) return '—';
    const c = this.contratoSelecionado;
    return `Contrato #${c.id} · ${this.fmtDisplay(c.inicioContrato)} – ${this.fmtDisplay(c.fimContrato)}`;
  }

  private fmt(d: any): string {
    if (!d) return '';
    return d instanceof Date ? d.toISOString().split('T')[0] : String(d);
  }
  fmtDisplay(d?: string): string {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt.getTime())
      ? d
      : dt.toLocaleDateString('pt-AO', { month: '2-digit', year: 'numeric' });
  }
  fmtMoeda(v?: number): string {
    if (v == null) return '—';
    return new Intl.NumberFormat('pt-AO', {
      style: 'currency',
      currency: 'AOA',
      maximumFractionDigits: 0,
    }).format(v);
  }
}
